import { useAsyncData, useNuxtApp } from '#app'
import { type MaybeRefOrGetter, type Ref, toValue } from 'vue'

import type { SearchForHits, SearchResponse } from 'algoliasearch-v5'

import type { SearchContext } from '../types'

import type {
  CachedAlgoliaData,
  UseAlgoliaSearchOptions,
} from './useAlgoliaSearch.types'

/**
 * Helper to parse clientMaxAge into milliseconds
 */
function parseClientMaxAge(maxAge?: number | 'permanent'): number | null {
  if (maxAge === undefined || maxAge === 0) {
    // No custom expiration
    return null
  }
  if (maxAge === 'permanent' || maxAge === -1) {
    // Never expire
    return -1
  }
  if (maxAge > 0) {
    // Convert seconds to milliseconds
    return maxAge * 1000
  }

  return null
}

export function useAlgoliaSearch<
  THit,
  DataT = SearchResponse<THit>[],
  DefaultT = DataT,
>(
  cacheKey: MaybeRefOrGetter<string>,
  searchContext: SearchContext,
  requests: Ref<SearchForHits[]>,
  options?: UseAlgoliaSearchOptions<THit, DataT, DefaultT>,
) {
  const nuxtApp = useNuxtApp()
  const { $algolia } = nuxtApp

  if (!$algolia) {
    throw new Error('[Algolia] Client not initialized')
  }

  const clientMaxAgeMs = parseClientMaxAge(options?.clientMaxAge)

  return useAsyncData(
    cacheKey,
    async () => {
      const requestsValue = requests.value

      await options?.onBeforeRequest?.({
        requests: requestsValue,
      })

      try {
        const { results } = (await $algolia.search<THit>(
          {
            requests: requestsValue,
          },
          {
            headers: {
              'BM-Search-Domain': searchContext,
            },
          },
        )) as { results: SearchResponse<THit>[] }

        await options?.onSuccess?.({
          requests: requestsValue,
          results,
        })

        // Transform the data manually (so we control when it happens)
        const data = options?.transform
          ? await options.transform(results)
          : (results as DataT)

        // Store in cache with expiration on client
        if (import.meta.client) {
          // Store first hydration time if not already set
          // eslint-disable-next-line no-underscore-dangle
          if (!nuxtApp.static.data.__firstHydrationTime) {
            // eslint-disable-next-line no-underscore-dangle
            nuxtApp.static.data.__firstHydrationTime = Date.now()
          }

          const cacheEntry: CachedAlgoliaData<DataT> = {
            data,
            expiresAt:
              clientMaxAgeMs === null || clientMaxAgeMs === -1
                ? Number.POSITIVE_INFINITY
                : Date.now() + clientMaxAgeMs,
          }

          nuxtApp.static.data[toValue(cacheKey)] = cacheEntry
        }

        // useAsyncData won't transform again because we set transform: undefined below
        return data
      } catch (error) {
        await options?.onError?.({
          requests: requestsValue,
          error,
        })

        // Re-throw the error to maintain existing error handling behavior
        throw error
      }
    },
    {
      getCachedData: (key, app, ctx) => {
        // Never return from cache when manually refreshing
        if (ctx.cause === 'refresh:manual' || ctx.cause === 'refresh:hook') {
          return undefined
        }

        // During hydration, always use payload data
        const payloadData: DataT | undefined = app.payload.data[key]
        if (payloadData && app.isHydrating) {
          return payloadData
        }

        // After hydration, check if payload data is still valid
        if (payloadData && clientMaxAgeMs && clientMaxAgeMs > 0) {
          const firstHydrationTime: number | undefined =
            // eslint-disable-next-line no-underscore-dangle
            app.static.data.__firstHydrationTime

          if (firstHydrationTime) {
            const expires = firstHydrationTime + clientMaxAgeMs

            if (expires > Date.now()) {
              return payloadData
            }
          }
        }

        // Check static cache with expiration
        const cachedEntry: CachedAlgoliaData<DataT> | undefined =
          app.static.data[key]

        if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
          return cachedEntry.data
        }

        return undefined
      },
      // Remove transform from options since we handle it manually
      ...options,
      transform: undefined,
    },
  )
}
